import { ClearFiltersButton } from "@/components/ui/ClearFiltersButton";
import { EmailWhatsappText } from "@/components/ui/EmailWhatsappText";
import { ErrorResult } from "@/components/ui/ErrorResult";
import {
  ModalCUUser,
  ModalCUUserRef,
} from "@/components/ui/modals/ModalCUUser";
import { SectionPrivateHeader } from "@/components/ui/SectionPrivateHeader";
import useColumnSearch from "@/hooks/table/useColumnSearch";
import useTableFilters from "@/hooks/table/useTableFilters";
import { useDeleteUser } from "@/services/users/mutation";
import { useUsers } from "@/services/users/queries";
import { User } from "@/types/user";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useAuth } from "@/auth/AuthProvider";
import { message, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useRef } from "react";

export const UsersPrivate = () => {
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === "auditor";
  const modalCURef = useRef<ModalCUUserRef>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const { tableParams, tableKey, resetFilters, handleTableChange } =
    useTableFilters();

  const { data, isLoading, isError, error, isFetching, refetch } =
    useUsers(tableParams);

  const { getColumnSearchProps } = useColumnSearch<User>();

  const baseColumns: ColumnsType<User> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },

    {
      title: "Correo electrónico",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
      render: (text: string) => <EmailWhatsappText value={text} type="email" />,
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      render: (text: User["role"]) => {
        const role_map = {
          admin: {
            color: "gold",
            label: "Administrador",
          },
          waiter: {
            color: "blue",
            label: "Mesero",
          },
          kitchen: {
            color: "green",
            label: "Cocina",
          },
          cashier: {
            color: "purple",
            label: "Cajero",
          },
          auditor: {
            color: "cyan",
            label: "Auditor",
          },
        };

        const role = role_map[text];

        if (!role) return null;

        return <Tag color={role.color}>{role.label}</Tag>;
      },
      filters: [
        {
          text: "Administrador",
          value: "admin",
        },
        {
          text: "Mesero",
          value: "waiter",
        },
        {
          text: "Cocina",
          value: "kitchen",
        },
        {
          text: "Cajero",
          value: "cashier",
        },
        {
          text: "Auditor",
          value: "auditor",
        },
      ],
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: () => {
        return <Tag color={"success"}>Activado</Tag>;
      },
    },
  ];

  const actionColumn: ColumnsType<User>[number] = {
    title: "Acciones",
    dataIndex: "id",
    key: "id",
    width: 100,
    render: (id: User["id"], record: User) => (
      <Space size="middle">
        {/* EDIT */}
        <Tooltip placement="top" className="cursor-pointer" title="Editar">
          <button
            onClick={() => {
              modalCURef.current?.childFunction(record);
            }}
          >
            <EditOutlined className="text-lg" />
          </button>
        </Tooltip>

        <Tooltip placement="top" className="cursor-pointer" title="Eliminar">
          <div>
            <Popconfirm
              title="¿Estás seguro/a que deseas desactivar a este usuario/a?"
              okText="Sí, Eliminar"
              cancelText="No, cancelar"
              cancelButtonProps={{
                style: { borderRadius: 12 },
              }}
              okButtonProps={{
                style: {
                  backgroundColor: "#ef4444",
                  border: 0,
                },
              }}
              placement="left"
              onConfirm={() => {
                onDeleteUser(id);
              }}
            >
              <button className="cursor-pointer">
                <DeleteOutlined className="text-lg" />
              </button>
            </Popconfirm>
          </div>
        </Tooltip>
      </Space>
    ),
  };

  const columns = isReadOnly ? baseColumns : [...baseColumns, actionColumn];

  const deleteUser = useDeleteUser();

  const onDeleteUser = async (id: User["id"]) => {
    deleteUser.mutate(id, {
      onSuccess: () => {
        messageApi.success(`El usuario/a se ha eliminado correctamente.`);
      },
      onError: () => {
        messageApi.error("Ups, algo salió mal. Intenta nuevamente.");
      },
    });
  };

  if (isError) {
    return <ErrorResult error={error} refetch={refetch} />;
  }

  return (
    <>
      {contextHolder}
      <SectionPrivateHeader
        title="Personal del Sistema"
        existsButton={!isReadOnly}
        onButtonClick={isReadOnly ? undefined : () => modalCURef.current?.childFunction()}
      />

      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-3">
        <div className="ml-auto max-w-fit">
          <span className="text-xs px-2 py-1 rounded-full bg-[#070B0E] text-gray-300 border border-[#1A2328]">
            {data?.total ?? 0} usuarios
          </span>
        </div>
        <ClearFiltersButton onClear={resetFilters} />
      </div>

      <Table<User>
        columns={columns}
        dataSource={data?.items}
        key={tableKey}
        pagination={{
          current: tableParams?.pagination.current,
          pageSize: tableParams?.pagination.pageSize,
          total: data?.total,
        }}
        onChange={handleTableChange}
        loading={isLoading || isFetching || deleteUser.isPending}
        scroll={{ x: 1000 }}
      />

      <ModalCUUser ref={modalCURef} />
    </>
  );
};
